// Barcode API Service for ZippyHealth Tracker
// Leverages local USDA database for food products and can be extended for medications

import { localUsdaApi, type LocalUSDAFood } from './localUsdaApi';
import { supabase } from './supabase';

export interface BarcodeProduct {
  type: 'food' | 'medication' | 'supplement';
  barcode: string;
  name: string;
  brand?: string;
  manufacturer?: string;
  description: string;
  image_url?: string;
  nutrition?: {
    calories: number;
    carbs: number;
    protein: number;
    fat: number;
    fiber: number;
    serving_size?: number;
    serving_unit?: string;
  };
  medication_info?: {
    ndc_code?: string;
    dosage_form?: string;
    strength?: string;
    active_ingredients?: string[];
  };
  data_source: string;
  confidence_score: number; // 0-100
}

export interface BarcodeScanResult {
  found: boolean;
  product?: BarcodeProduct;
  suggestions?: BarcodeProduct[];
  error?: string;
}

class BarcodeApiService {
  private readonly maxSuggestions = 5;

  // Main barcode lookup function
  async lookupBarcode(barcode: string): Promise<BarcodeScanResult> {
    try {
      // Clean and validate barcode
      const cleanBarcode = this.cleanBarcode(barcode);
      if (!this.isValidBarcode(cleanBarcode)) {
        return {
          found: false,
          error: 'Invalid barcode format'
        };
      }

      // Try USDA database first (highest confidence)
      const usdaResult = await this.lookupUSDABarcode(cleanBarcode);
      if (usdaResult.found && usdaResult.product) {
        return usdaResult;
      }

      // Try alternative barcode formats
      const alternativeResults = await this.tryAlternativeFormats(cleanBarcode);
      if (alternativeResults.found) {
        return alternativeResults;
      }

      // If no exact match, try partial matches or suggestions
      const suggestions = await this.getSimilarProducts(cleanBarcode);
      
      return {
        found: false,
        suggestions,
        error: suggestions.length > 0 ? 'No exact match found, showing similar products' : 'No products found for this barcode'
      };

    } catch (error) {
      console.error('Barcode lookup error:', error);
      return {
        found: false,
        error: 'Barcode lookup service temporarily unavailable'
      };
    }
  }

  // Lookup in USDA database
  private async lookupUSDABarcode(barcode: string): Promise<BarcodeScanResult> {
    try {
      const food = await localUsdaApi.getFoodByUPC(barcode);
      
      if (food) {
        const product = this.convertUSDAToProduct(food, barcode);
        return {
          found: true,
          product
        };
      }

      return { found: false };
    } catch (error) {
      console.error('USDA barcode lookup error:', error);
      return { found: false };
    }
  }

  // Try alternative barcode formats (EAN-13, UPC-A, etc.)
  private async tryAlternativeFormats(barcode: string): Promise<BarcodeScanResult> {
    const alternatives = this.generateBarcodeAlternatives(barcode);
    
    for (const altBarcode of alternatives) {
      const result = await this.lookupUSDABarcode(altBarcode);
      if (result.found) {
        return result;
      }
    }

    return { found: false };
  }

  // Convert USDA food to barcode product
  private convertUSDAToProduct(food: LocalUSDAFood, barcode: string): BarcodeProduct {
    const macros = localUsdaApi.extractMacros(food);
    
    return {
      type: 'food',
      barcode,
      name: food.description,
      brand: food.brand_name || food.brand_owner,
      manufacturer: food.brand_owner,
      description: food.description,
      nutrition: {
        calories: macros.calories,
        carbs: macros.carbs,
        protein: macros.protein,
        fat: macros.fat,
        fiber: macros.fiber,
        serving_size: food.serving_size,
        serving_unit: food.serving_size_unit
      },
      data_source: `USDA ${food.data_type}`,
      confidence_score: this.calculateConfidenceScore(food)
    };
  }

  // Calculate confidence score based on data quality
  private calculateConfidenceScore(food: LocalUSDAFood): number {
    let score = 50; // Base score

    // Data source quality bonus
    switch (food.data_type) {
      case 'Foundation':
        score += 40; // Highest quality - lab analyzed
        break;
      case 'Branded':
        score += 35; // High quality - manufacturer data
        break;
      case 'Survey (FNDDS)':
        score += 25;
        break;
      case 'SR Legacy':
        score += 20;
        break;
      case 'Experimental':
        score += 10;
        break;
    }

    // Completeness bonus
    if (food.brand_name) score += 5;
    if (food.brand_owner) score += 5;
    if (food.serving_size) score += 5;
    if (food.ingredients) score += 5;
    if (food.nutrients && food.nutrients.length > 10) score += 10;

    return Math.min(score, 100);
  }

  // Get similar products when exact match not found
  private async getSimilarProducts(barcode: string): Promise<BarcodeProduct[]> {
    try {
      // Extract potential brand or product info from barcode
      const searchTerms = this.extractSearchTermsFromBarcode(barcode);
      
      if (searchTerms.length === 0) {
        return [];
      }

      const suggestions: BarcodeProduct[] = [];

      for (const term of searchTerms) {
        const { data, error } = await supabase
          .from('usda_foods')
          .select('*')
          .or(`description.ilike.%${term}%,brand_name.ilike.%${term}%,brand_owner.ilike.%${term}%`)
          .not('gtin_upc', 'is', null) // Only products with barcodes
          .order('data_source_priority')
          .limit(this.maxSuggestions);

        if (!error && data) {
          for (const food of data) {
            if (suggestions.length < this.maxSuggestions) {
              suggestions.push(this.convertUSDAToProduct(food, food.gtin_upc || ''));
            }
          }
        }
      }

      return suggestions;
    } catch (error) {
      console.error('Similar products lookup error:', error);
      return [];
    }
  }

  // Clean and normalize barcode
  private cleanBarcode(barcode: string): string {
    return barcode.replace(/[^0-9]/g, '').trim();
  }

  // Validate barcode format
  private isValidBarcode(barcode: string): boolean {
    // Common barcode lengths: UPC-A (12), EAN-13 (13), EAN-8 (8)
    const validLengths = [8, 12, 13, 14];
    return validLengths.includes(barcode.length) && /^\d+$/.test(barcode);
  }

  // Generate alternative barcode formats
  private generateBarcodeAlternatives(barcode: string): string[] {
    const alternatives: string[] = [];
    
    // UPC-A to EAN-13 conversion (add leading zero)
    if (barcode.length === 12) {
      alternatives.push('0' + barcode);
    }
    
    // EAN-13 to UPC-A conversion (remove leading zero)
    if (barcode.length === 13 && barcode.startsWith('0')) {
      alternatives.push(barcode.substring(1));
    }
    
    // Try with/without check digit
    if (barcode.length > 8) {
      alternatives.push(barcode.substring(0, barcode.length - 1));
      alternatives.push(barcode + '0');
    }

    return alternatives;
  }

  // Extract search terms from barcode (basic heuristics)
  private extractSearchTermsFromBarcode(barcode: string): string[] {
    const terms: string[] = [];
    
    // GS1 company prefix lookup (basic implementation)
    // This would typically use a GS1 company database
    const companyPrefixes: { [key: string]: string } = {
      '01': 'Coca-Cola',
      '02': 'PepsiCo',
      '03': 'Nestlé',
      '04': 'Unilever',
      '05': 'General Mills',
      '06': 'Kellogg',
      '07': 'Kraft',
      '08': 'Campbell',
      '09': 'Del Monte',
      '10': 'Heinz'
    };

    // Extract first 2-3 digits as potential company prefix
    const prefix2 = barcode.substring(0, 2);
    const prefix3 = barcode.substring(0, 3);
    
    if (companyPrefixes[prefix2]) {
      terms.push(companyPrefixes[prefix2]);
    }
    if (companyPrefixes[prefix3]) {
      terms.push(companyPrefixes[prefix3]);
    }

    return terms;
  }

  // Get barcode statistics
  async getBarcodeStats(): Promise<{
    totalBarcodes: number;
    foodBarcodes: number;
    brandedFoods: number;
    dataTypes: string[];
  }> {
    try {
      const { count: totalBarcodes, error: totalError } = await supabase
        .from('usda_foods')
        .select('*', { count: 'exact', head: true })
        .not('gtin_upc', 'is', null);

      const { count: brandedCount, error: brandedError } = await supabase
        .from('usda_foods')
        .select('*', { count: 'exact', head: true })
        .eq('data_type', 'Branded')
        .not('gtin_upc', 'is', null);

      const { data: dataTypes, error: typesError } = await supabase
        .from('usda_foods')
        .select('data_type')
        .not('gtin_upc', 'is', null);

      const uniqueTypes = Array.from(new Set(dataTypes?.map(d => d.data_type) || []));

      return {
        totalBarcodes: totalBarcodes || 0,
        foodBarcodes: totalBarcodes || 0, // All our barcodes are food currently
        brandedFoods: brandedCount || 0,
        dataTypes: uniqueTypes
      };
    } catch (error) {
      console.error('Barcode stats error:', error);
      return {
        totalBarcodes: 0,
        foodBarcodes: 0,
        brandedFoods: 0,
        dataTypes: []
      };
    }
  }

  // Search for products with barcodes
  async searchProductsWithBarcodes(query: string, limit = 20): Promise<BarcodeProduct[]> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .not('gtin_upc', 'is', null)
        .or(`description.ilike.%${query}%,brand_name.ilike.%${query}%,brand_owner.ilike.%${query}%`)
        .order('data_source_priority')
        .limit(limit);

      if (error) {
        throw new Error(`Search failed: ${error.message}`);
      }

      return (data || []).map(food => 
        this.convertUSDAToProduct(food, food.gtin_upc || '')
      );
    } catch (error) {
      console.error('Barcode search error:', error);
      return [];
    }
  }

  // Validate that barcode scanning is available
  async isBarcodeServiceAvailable(): Promise<boolean> {
    try {
      const stats = await this.getBarcodeStats();
      return stats.totalBarcodes > 0;
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
export const barcodeApi = new BarcodeApiService();
export default barcodeApi; 
